import {Injectable} from '@angular/core';
import {ThemeColors} from './theme-map';
import {AziendaModel} from '../../shared/models';

const PALETTE_LEVELS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

@Injectable({providedIn: 'root'})
export class ThemeService {
	/**
	 * Applica il tema dell'azienda corrente: se sono presenti colori di palette personalizzati
	 * (configurati dall'admin nel dialog azienda) li usa per generare dinamicamente il tema
	 */
	applyThemeFromAzienda(azienda?: AziendaModel) {
		// p-colorPicker (PrimeNG) salva l'hex senza "#" iniziale: normalizziamo prima di usarlo come colore CSS
		const colorePrimario = this.normalizeHex(azienda?.palette?.colorePrimario ?? '#0085a4');
		const coloreSecondario = azienda?.palette?.coloreSecondario ? this.normalizeHex(azienda.palette.coloreSecondario) : '#901b06';

		const config: ThemeColors = {
			primary: colorePrimario,
			primaryLight: this.lighten(colorePrimario, 25),
			primaryHover: this.toRgba(colorePrimario, 0.2),
			primaryActive: this.lighten(colorePrimario, 5),
			primaryDarker: this.darken(colorePrimario, 15),
			primaryText: this.contrastColor(colorePrimario),
			primaryPalette: this.generateShadesFromColor(colorePrimario),

			secondary: coloreSecondario,
			secondaryContrastColor: this.contrastColor(coloreSecondario),
			secondaryHover: this.toRgba(coloreSecondario, 0.55),
			secondaryActive: this.lighten(coloreSecondario, 15),
			secondaryText: this.contrastColor(coloreSecondario)
		};
		this.applyThemeFromConfig(config);
	}

	private applyThemeFromConfig(config: ThemeColors) {
		const mapping: Record<string, string> = {
			primary: '--primary-color',
			primaryLight: '--primary-color-light',
			primaryHover: '--primary-color-hover',
			primaryActive: '--primary-color-active',
			primaryDarker: '--primary-color-darker',
			primaryText: '--primary-color-text',
			secondary: '--secondary-color',
			secondaryContrastColor: '--secondary-contrastColor',
			secondaryHover: '--secondary-hoverColor',
			secondaryActive: '--secondary-activeColor',
			secondaryText: '--secondary-text'
		};

		Object.keys(mapping).forEach(key => {
			if (config[key]) {
				document.documentElement.style.setProperty(mapping[key], config[key]);
			}
		});

		if (config.primaryPalette) {
			Object.entries(config.primaryPalette).forEach(([level, value]) => {
				document.documentElement.style.setProperty(`--p-primary-${level}`, value);
			});
		}

	}

	/**
	 * Genera una scala di colori (50 -> 950, stile PrimeNG/Tailwind) a partire da un singolo colore base,
	 * facendo variare la luminosità in spazio HSL mantenendo tonalità e saturazione.
	 */
	private generateShadesFromColor(hex: string): {[key: string]: string} {
		const [h, s, l] = this.hexToHsl(hex);
		const baseIndex = PALETTE_LEVELS.indexOf(500);
		const shades: {[key: string]: string} = {};
		PALETTE_LEVELS.forEach((level, index) => {
			let newL: number;
			if (index === baseIndex) {
				newL = l;
			} else if (index < baseIndex) {
				const t = (baseIndex - index) / baseIndex;
				newL = l + (97 - l) * t;
			} else {
				const t = (index - baseIndex) / (PALETTE_LEVELS.length - 1 - baseIndex);
				newL = l - (l - 10) * t;
			}
			shades[level] = this.hslToHex(h, s, Math.max(0, Math.min(100, newL)));
		});
		return shades;
	}

	private lighten(hex: string, amount: number): string {
		const [h, s, l] = this.hexToHsl(hex);
		return this.hslToHex(h, s, Math.min(100, l + amount));
	}

	private darken(hex: string, amount: number): string {
		const [h, s, l] = this.hexToHsl(hex);
		return this.hslToHex(h, s, Math.max(0, l - amount));
	}

	private toRgba(hex: string, alpha: number): string {
		const [r, g, b] = this.hexToRgb(hex);
		return `rgba(${r}, ${g}, ${b}, ${alpha})`;
	}

	/** Bianco o nero, in base alla luminanza relativa del colore, per garantire un testo leggibile sopra. */
	private contrastColor(hex: string): string {
		const [r, g, b] = this.hexToRgb(hex);
		const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
		return luminance > 0.6 ? '#000000' : '#ffffff';
	}

	private normalizeHex(hex: string): string {
		return hex.startsWith('#') ? hex : `#${hex}`;
	}

	private hexToRgb(hex: string): [number, number, number] {
		let normalized = hex.replace('#', '');
		if (normalized.length === 3) {
			normalized = normalized.split('').map(c => c + c).join('');
		}
		const r = parseInt(normalized.substring(0, 2), 16);
		const g = parseInt(normalized.substring(2, 4), 16);
		const b = parseInt(normalized.substring(4, 6), 16);
		return [r, g, b];
	}

	private hexToHsl(hex: string): [number, number, number] {
		const [r, g, b] = this.hexToRgb(hex).map(v => v / 255);
		const max = Math.max(r, g, b);
		const min = Math.min(r, g, b);
		let h = 0;
		let s = 0;
		const l = (max + min) / 2;
		const delta = max - min;
		if (delta !== 0) {
			s = delta / (1 - Math.abs(2 * l - 1));
			switch (max) {
				case r:
					h = 60 * (((g - b) / delta) % 6);
					break;
				case g:
					h = 60 * ((b - r) / delta + 2);
					break;
				default:
					h = 60 * ((r - g) / delta + 4);
			}
		}
		if (h < 0) {
			h += 360;
		}
		return [h, s * 100, l * 100];
	}

	private hslToHex(h: number, s: number, l: number): string {
		s /= 100;
		l /= 100;
		const c = (1 - Math.abs(2 * l - 1)) * s;
		const x = c * (1 - Math.abs((h / 60) % 2 - 1));
		const m = l - c / 2;
		let r = 0;
		let g = 0;
		let b = 0;
		if (h < 60) {
			[r, g, b] = [c, x, 0];
		} else if (h < 120) {
			[r, g, b] = [x, c, 0];
		} else if (h < 180) {
			[r, g, b] = [0, c, x];
		} else if (h < 240) {
			[r, g, b] = [0, x, c];
		} else if (h < 300) {
			[r, g, b] = [x, 0, c];
		} else {
			[r, g, b] = [c, 0, x];
		}
		const toHex = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
		return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
	}

}
